import { IssueRefusal, issueTexts, reportError, syncHistoryFields } from "@nexxt/app-kit";
import type { ValidationIssue } from "@nexxt/contracts";
import type { InboundDocumentKind, ProcurementIssueRule } from "@nexxt/contracts/procurement";
import { inboundDocumentKindLabel } from "../inbound/inbound-texts";
import { m } from "../paraglide/messages.js";
import { procurementTexts } from "../procurement-texts";

// What the procurement domain refuses, in the screens' words: the rules of its issues (422 `details.issues`), the
// names of the fields they point at, and what a failed write says when the screen does not. The fields of a PO, its
// lines, its proposals and its inbounds are named here; the others by the app (`registerProcurementTexts`, ADR-169).

const rules: Record<ProcurementIssueRule, () => string> = {
  unknown_code: m.issue_unknown_code,
  after_start: m.issue_after_start,
  not_empty: m.issue_not_empty,
  unknown_line: m.po_issue_unknown_line,
  duplicate_line: m.issue_duplicate_line,
  below_received: m.po_issue_below_received,
  already_received: m.po_issue_already_received,
  cost_locked: m.po_issue_cost_locked,
  not_open: m.po_issue_not_open,
  change_pending: m.po_issue_change_pending,
  changes_pending: m.po_issue_changes_pending,
  not_a_reduction: m.po_issue_not_a_reduction,
  unknown_sku: m.po_issue_unknown_sku,
  same_sku: m.po_issue_same_sku,
  swap_by_tomorrow: m.po_issue_swap_by_tomorrow,
  unknown_change: m.po_issue_unknown_change,
  duplicate_change: m.po_issue_duplicate_change,
  not_pending: m.po_issue_not_pending,
  not_synced: m.issue_not_synced,
  missing_mapping: m.issue_missing_mapping,
  ambiguous_mapping: m.issue_ambiguous_mapping,
  invalid_value: m.issue_invalid_value,
  required: m.issue_required,
  po_not_confirmed: m.inbound_issue_po_not_confirmed,
  missing_document: m.inbound_issue_missing_document,
  unknown_file: m.inbound_issue_unknown_file,
  wrong_folder: m.inbound_issue_wrong_folder,
  duplicate_file: m.inbound_issue_duplicate_file,
  too_many: m.inbound_issue_too_many,
  overlap: m.issue_overlap,
  credit_note_only: m.inbound_issue_credit_note_only,
  no_credit_note: m.inbound_issue_no_credit_note,
  extraction_off: m.inbound_issue_extraction_off,
};

const { ruleText, fieldIssue } = issueTexts(rules);

/** The issue of a field, for the form that shows it next to the control. */
export const procurementFieldIssue = fieldIssue;

/**
 * Names of the fields of a PO, of its lines, of the proposals on them and of its inbounds: in forms, grids, issues,
 * history and the errors of the sync to Business Central.
 */
const fieldLabels: Record<string, () => string> = {
  paymentTerms: m.po_field_payment_terms,
  incoterm: m.po_field_incoterm,
  shipTo: m.po_field_ship_to,
  shippingMethod: m.po_field_shipping_method,
  deliveryWindowOpen: m.po_field_delivery_from,
  deliveryWindowClose: m.po_field_delivery_to,
  memo: m.po_field_memo,
  lines: m.col_lines,
  changes: m.po_tab_changes,
  status: m.col_status,
  qtyOrdered: m.po_col_qty_ordered,
  unitCost: m.po_col_unit_cost,
  expectedReceiptDate: m.po_col_expected_receipt,
  vendorCode: m.po_col_vendor_code,
  description: m.po_col_description,
  closureReason: m.po_col_closure_reason,
  type: m.po_change_col_type,
  proposedQty: m.po_change_field_proposed_qty,
  proposedSkuId: m.po_change_field_proposed_sku,
  skuId: m.field_sku,
  sku: m.field_sku,
  revision: m.po_field_revision,
  closedLines: m.po_field_closed_lines,
  supplier: m.field_supplier,
  externalId: m.po_sync_field_external_id,
  location: m.po_col_destination,
  paymentTermsCode: m.po_field_payment_terms,
  season: m.col_season,
  brand: m.col_brand,
  ...syncHistoryFields,
  // the inbound (ADR-128): its header, lines, documents and notes
  poId: m.inbound_field_purchase_order,
  purchaseOrderId: m.inbound_field_purchase_order,
  documents: m.inbound_field_documents,
  invoiceNumber: m.inbound_field_invoice_number,
  invoiceDate: m.inbound_field_invoice_date,
  invoiceNetTotal: m.inbound_field_invoice_net_total,
  invoiceVatAmount: m.inbound_field_invoice_vat,
  invoiceTotal: m.inbound_field_invoice_total,
  packingListNumber: m.inbound_field_packing_list_number,
  readyDateForPickup: m.inbound_field_ready_for_pickup,
  pickupContactName: m.inbound_field_pickup_contact,
  pickupPhone: m.inbound_field_pickup_phone,
  pickupEmail: m.inbound_field_pickup_email,
  expectedDeliveryDate: m.inbound_field_expected_delivery,
  deliveryNumber: m.inbound_field_delivery_number,
  totalCartons: m.inbound_field_cartons,
  totalPallets: m.inbound_field_pallets,
  grossWeightKg: m.inbound_field_gross_weight,
  netWeightKg: m.inbound_field_net_weight,
  boxDimensions: m.inbound_field_box_dimensions,
  palletDimensions: m.inbound_field_pallet_dimensions,
  contentDetails: m.inbound_field_content,
  comments: m.inbound_field_comments,
  qtyDeclared: m.inbound_col_qty_declared,
  shortShipmentAccepted: m.inbound_col_short_shipment,
  kind: m.inbound_col_document_kind,
  filename: m.inbound_col_file,
  body: m.field_note,
  poLineId: m.field_line,
  ean: m.col_ean,
  // ADR-140: the choice for the missing units, and the file of a credit note
  resolution: m.inbound_settle_choice,
  fileId: m.inbound_col_file,
  // ADR-233: the reading of the packing list by the extraction tool
  extraction: m.inbound_field_extraction,
  extractionStatus: m.inbound_field_extraction_status,
  extractionRevision: m.inbound_field_extraction_revision,
  lowConfidenceLines: m.inbound_field_low_confidence_lines,
};

/** The value of an issue in the screens' words, where it is a code: the kind of a missing document. */
const valueTexts: Record<string, (value: string) => string | undefined> = {
  documents: (value) => inboundDocumentKindLabel[value as InboundDocumentKind]?.(),
};

/** The name of a field: the app's words first, then the screens'; the raw name when neither knows it. */
export const procurementFieldLabel = (field: string): string =>
  procurementTexts().fieldText?.(field) ?? fieldLabels[field]?.() ?? field;

/**
 * Fields that name the item itself, which `row` already says: the rule alone is the sentence. A file and a PO line
 * are named by `row` too, by their name and their number, never by their id.
 */
const ITSELF = new Set(["id", "lineId", "changeId", "changeIds", "fileId", "poLineId"]);

/**
 * A problem in words. `row` is the position of the item in the request, which `rowName` turns into what the user
 * sees (a line number); the issues of a confirmation and of a sync carry the line number itself.
 */
function issueText({ field, rule, row, value }: ValidationIssue, rowName: RowName): string {
  const label = ITSELF.has(field) ? undefined : procurementFieldLabel(field);
  const shown =
    value === undefined
      ? undefined
      : (procurementTexts().valueText?.(field, value) ?? valueTexts[field]?.(value) ?? value);
  const subject = label !== undefined && shown !== undefined ? `${label} “${shown}”` : label;
  const text = [subject, ruleText(rule)].filter(Boolean).join(": ");
  return row === undefined ? text : `${rowName(row)}: ${text}`;
}

type RowName = (row: number) => string;

interface RefusedProps {
  error: unknown;
  title: string;
  /** How a `row` of the issues is named: by default it is the number of a PO line. */
  rowName?: RowName;
}

/** Why the domain refused an action on a PO (422), next to what asked for it: every issue by field and line. */
export function ProcurementRefusal({ error, title, rowName = lineNumber }: RefusedProps) {
  return <IssueRefusal error={error} title={title} text={(issue) => issueText(issue, rowName)} />;
}

const lineNumber: RowName = (lineNo) => m.line_number({ lineNo });

/**
 * How the issues of a request name its rows: `lineNos` are the numbers of the lines the request was about, in its
 * order. A line the screen no longer shows is named by its position.
 */
export const sentLines =
  (lineNos: readonly (number | undefined)[]): RowName =>
  (row) => {
    const lineNo = lineNos[row];
    return lineNo === undefined ? m.sync_run_error_row({ row: row + 1 }) : lineNumber(lineNo);
  };

/** A write on a PO, its lines or its proposals failed: what the screen does not show itself. */
export const reportProcurementError = (error: unknown, vars?: unknown) =>
  reportError(error, m.po_changed_meanwhile, [], vars);
